import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * Opt-in schedule views (LBS, …). Always available as possibilities;
 * checking a box surfaces that view in the sidebar menu.
 */
const ScheduleModulesPanel = ({
  scopeType, // 'estimate' | 'job'
  scopeKey,
  title = 'Schedule options',
  compact = false,
  onChange,
}) => {
  const { instance, accounts } = useMsal();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!scopeType || !scopeKey) return;
    try {
      setLoading(true);
      setError(null);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/schedule-modules/${scopeType}/${encodeURIComponent(scopeKey)}`
      );
      setModules(res.data?.data?.modules || []);
    } catch (e) {
      console.error('schedule modules load:', e);
      setError(e.response?.data?.error || 'Failed to load schedule options');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, scopeType, scopeKey]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (moduleKey, enabled) => {
    try {
      setSavingKey(moduleKey);
      setError(null);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/schedule-modules/${scopeType}/${encodeURIComponent(scopeKey)}`,
        { moduleKey, enabled }
      );
      const next = res.data?.data?.modules || [];
      setModules(next);
      onChange?.(res.data?.data?.enabled || {});
      // Notify sidebar / other listeners in this tab
      try {
        window.dispatchEvent(new CustomEvent('schedule-modules-changed', {
          detail: { scopeType, scopeKey, enabled: res.data?.data?.enabled || {} },
        }));
      } catch { /* ignore */ }
    } catch (e) {
      console.error('schedule modules save:', e);
      setError(e.response?.data?.error || 'Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  if (!scopeKey) {
    return (
      <div style={{ padding: compact ? 12 : 24, color: '#64748b', fontSize: 14 }}>
        Select {scopeType === 'job' ? 'a project' : 'an estimate'} to configure schedule options.
      </div>
    );
  }

  return (
    <div style={{ padding: compact ? 0 : '1rem 1.25rem' }}>
      {!compact && (
        <>
          <h2 style={{ margin: '0 0 6px', fontSize: 20, color: '#0f172a' }}>{title}</h2>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b', maxWidth: 640 }}>
            Optional schedule views for this {scopeType}. Leave unchecked until you need them —
            they stay available here as possibilities. Checking a box adds it to the sidebar menu.
          </p>
        </>
      )}
      {loading && <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>}
      {error && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#b91c1c', fontSize: 13 }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
        {modules.map((m) => (
          <label
            key={m.key}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              padding: '12px 14px',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              background: m.enabled ? '#f0fdfa' : '#fff',
              cursor: savingKey === m.key ? 'wait' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!!m.enabled}
              disabled={savingKey === m.key}
              onChange={(e) => toggle(m.key, e.target.checked)}
              style={{ marginTop: 3, width: 16, height: 16, accentColor: '#0f766e' }}
            />
            <span>
              <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{m.label}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.45 }}>{m.description}</div>
              {m.enabled && (
                <div style={{ fontSize: 11, color: '#0f766e', marginTop: 6, fontWeight: 600 }}>
                  Enabled — appears in the Schedule / Proposal Schedule menu
                </div>
              )}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default ScheduleModulesPanel;
